import { getAddressInitialValues } from '../../utils';
import { EMPTY_ASSET_MANAGER, EMPTY_CLIENT_REPRESENTATIVE } from './constants';
import api from '~/libs/api';
import {
  IUpdateStatusesProps,
  TAssetManager,
  TManagersAndRepresentatives,
  TStatusResponse,
  ManagerVerificationStatus,
} from './types';
import * as Sentry from '@sentry/react';
import CryptoJS from 'crypto-js';
import { SYNC_TRIGGER_FIELDS } from '~/modules/identification/forms/ManagementStructure/constants.ts';

export const getInitialValues = (formData: TManagersAndRepresentatives) => ({
  assetManagers:
    formData?.assetManagers && formData?.assetManagers?.length > 0
      ? formData.assetManagers.map((manager) => ({
          id: manager?.id || null,
          lastName: manager?.lastName || null,
          firstName: manager?.firstName || null,
          middleName: manager?.middleName || null,
          identificationNumber: manager?.identificationNumber || null,
          foreignTaxNumber: manager?.foreignTaxNumber,
          contactNumbers: manager?.contactNumbers?.length || 0 ? manager.contactNumbers : [''],
          emailAddress: manager?.emailAddress?.length || 0 ? manager.emailAddress : [''],
          position: manager?.position || null,
          signatureAuthorityDetails: manager?.signatureAuthorityDetails || null,
          verificationStatus: manager?.verificationStatus || null,
          authorityTermType: manager?.authorityTermType || null,
          authorityExpirationDate: manager?.authorityExpirationDate || null,
          accessAfterAuthorityExpiration: manager?.accessAfterAuthorityExpiration || null,
          isHead: manager?.isHead || false,
        }))
      : [EMPTY_ASSET_MANAGER],
  isUnifiedAccountAccess: formData?.isUnifiedAccountAccess ?? true,
  isClientRepresentativesAbsent: formData?.isClientRepresentativesAbsent || false,
  isAnyAssetManagerDataChanged: formData?.isAnyAssetManagerDataChanged || false,
  clientRepresentatives: formData?.isClientRepresentativesAbsent
    ? []
    : formData?.clientRepresentatives && formData?.clientRepresentatives?.length
      ? formData.clientRepresentatives.map((represent) => ({
          lastName: represent?.lastName || null,
          firstName: represent?.firstName || null,
          middleName: represent?.middleName || null,
          identificationNumber: represent?.identificationNumber || null,
          foreignTaxNumber: represent?.foreignTaxNumber,
          citizenshipCountry: represent?.citizenshipCountry || null,
          residenceCountry: represent?.residenceCountry || null,
          address: getAddressInitialValues(represent),
        }))
      : [EMPTY_CLIENT_REPRESENTATIVE],
});

export const checkVerificationStatus = (assetManagers: TAssetManager[] | null) => {
  if (!assetManagers) return null;
  return assetManagers.map((value) => ({
    ...value,
    verificationStatus: value.verificationStatus || ManagerVerificationStatus.IN_PROGRESS,
  }));
};

export const updateStatuses = async ({
  assetManagers,
  applicationId,
  callbackFunc,
}: IUpdateStatusesProps) => {
  try {
    const {
      data: { assetManagers: response },
    }: TStatusResponse = await api.get(
      `identification/application/${applicationId}/asset-managers/statuses`
    );

    response?.forEach((manager) => {
      const index =
        assetManagers?.findIndex(
          (mng) =>
            manager.assetManagerId ===
            CryptoJS.MD5([mng.contactNumbers, mng.emailAddress].flat().join('|')).toString()
        ) ?? -1;
      if (index >= 0) {
        callbackFunc(index, manager);
      }
    });
  } catch (error) {
    Sentry.captureException(error);
  }
};

export const hasAssetManagersSyncFields = (dirtyFields: Record<string, any>): boolean => {
  if (!dirtyFields) return false;

  const topLevelChanged = SYNC_TRIGGER_FIELDS.some((field) => Boolean(dirtyFields[field]));
  if (topLevelChanged) return true;

  if (Array.isArray(dirtyFields.governingPersons)) {
    return dirtyFields.governingPersons.some(
      (person) => person && SYNC_TRIGGER_FIELDS.some((field) => Boolean(person[field]))
    );
  }

  return false;
};
